import React, { useMemo } from 'react';
import { Avatar, Box, IconButton, InputAdornment } from '@mui/material';
import { Logout } from '@mui/icons-material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Timer from '../../Pages/Timer/Timer';
import { APP_ROUTES, getTextFirstUpperLater } from '@/util';
import { AuthenticatedUser } from '@/model';
import { useListUserGroupsToPreloadQuery } from '@/hooks';
import { useUser } from '@hclinear/hc-linear-react';
import { useLocation } from '@tanstack/react-router';

const buildAvatarText = (authenticatedUser: AuthenticatedUser) => {
  if (!authenticatedUser) return '';
  return `${getTextFirstUpperLater(authenticatedUser.lastName)}${getTextFirstUpperLater(authenticatedUser.firstName)}`;
};

export const useRightMenuPart = (
  handleClick: (event: React.MouseEvent<HTMLElement>) => void,
  onLogoutClick: () => void,
  open: boolean,
  onTimerExpire: any,
) => {
  const INITIAL_TIME_VALUE = 120;
  const location = useLocation();
  const currentPath = location.pathname;

  const { user: authenticatedUser } = useUser();
  useListUserGroupsToPreloadQuery();

  return useMemo(() => {
    const menuItems = [
      {
        id: 'user-profile',
        icon: (
          <IconButton
            id='profile-menu-button'
            style={{ width: 'auto', height: 'auto' }}
            onClick={handleClick}
            aria-controls={open ? 'profile-menu' : undefined}
            aria-haspopup='true'
          >
            <Avatar alt={authenticatedUser?.username} className='profile-menu-icon'>
              {buildAvatarText(authenticatedUser)}
            </Avatar>
          </IconButton>
        ),
      },
      {
        id: 'logout',
        icon: (
          <InputAdornment position='end' onClick={onLogoutClick} className='logout-icon'>
            <Logout />
          </InputAdornment>
        ),
      },
    ];

    const timer =
      currentPath === APP_ROUTES.DASHBOARD
        ? {
            id: 'session-timer',
            icon: (
              <Box style={{ position: 'relative', width: '60px', height: '60px' }}>
                <Timer initialTime={INITIAL_TIME_VALUE} onExpire={onTimerExpire} />
              </Box>
            ),
          }
        : null;

    return timer ? [timer, ...menuItems] : menuItems;
  }, [authenticatedUser?.username, handleClick, onLogoutClick, open, currentPath]);
};

export const useProfileMenuItemPart = (
  t: (key: string) => string,
  userName: string,
  handleClose: () => void,
  navigateToAdmin: () => void,
  navigateToProfile: () => void,
  hasAuthViewPerm?: boolean,
) => {
  return useMemo(() => {
    const menuItems = [
      {
        id: 'profile',
        text: t('common.my-details'),
        icon: null,
        onClick: navigateToProfile,
      },
    ];
    // {
    //   id: 'general-settings',
    //   text: t('common.general_settings'),
    //   icon: (
    //     <InputAdornment
    //       position='start'
    //       style={{ width: '20px', height: '20px' }}
    //     >
    //       <AccountSetupIcon />
    //     </InputAdornment>
    //   ),
    //   onClick: handleClose,
    // },
    // {
    //   id: 'change-password',
    //   text: t('common.change_password'),
    //   icon: (
    //     <InputAdornment
    //       position='start'
    //       style={{
    //         width: '20px',
    //         height: '20px',
    //         marginBottom: '3px',
    //       }}
    //     >
    //       <ChangePasswordIcon />
    //     </InputAdornment>
    //   ),
    //   onClick: handleClose,
    // },

    if (hasAuthViewPerm) {
      menuItems.push({
        id: 'redirect-to-admin',
        text: t('common.administration'),
        icon: (
          <InputAdornment position='start' style={{ width: '20px', height: '20px' }}>
            <ManageAccountsIcon className='menu-icon' />
          </InputAdornment>
        ),
        onClick: navigateToAdmin,
      });
    }

    return menuItems;
  }, [t, userName, handleClose, navigateToAdmin]);
};
